import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  ERBRINGUNGSNAME,
  FAKTURIERUNGSNAME,
  HERKUNFTSNAME,
  STATUSNAME,
  type Auftragsstatus,
  type Auftragszeile,
  type Fakturierung,
  liste,
} from '../api/auftraege';
import { ApiFehler } from '../api/fehler';
import { markenklasse, type Markenart } from '../marken';
import { Meldung } from '../komponenten/Meldung';
import type { Filtermarke } from '../komponenten/Filtermarken';
import { Liste } from '../komponenten/Liste';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { KundeVerweis } from '../komponenten/Kontaktkarte';
import { alsEuro } from '../betrag';
import { alsTag } from '../datum';

type Filter = {
  readonly suche: string;
  readonly status: Auftragsstatus | '';
  readonly fakturierung: Fakturierung | '';
  readonly alleVersionen: boolean;
};

const LEER: Filter = { suche: '', status: '', fakturierung: '', alleVersionen: false };

/** WCAG 1.4.1: Der Zustand steht als Text da, die Farbe kommt hinzu. */
export const STATUSMARKE: Readonly<Record<Auftragsstatus, Markenart>> = {
  entwurf: 'stillgelegt',
  bestaetigt: 'abschluss',
  storniert: 'sackgasse',
};

const FAKTURIERUNGSMARKE: Readonly<Record<Fakturierung, Markenart>> = {
  /* Beide laufen — was sie unterscheidet, sagt ihr Wort (WCAG 1.4.1). */
  offen: 'laufend',
  teilweise: 'laufend',
  berechnet: 'abschluss',
};

/**
 * Die Auftragsliste — der Einstieg in den Bereich „Aufträge".
 *
 * Seitentyp Liste (ÄA-41): Zähler und „Neuer Auftrag" im Seitenkopf — die
 * Arbeitsfläche bietet die Anlage dort an, wo das Recht besteht; in der
 * Filterleiste steht sie deshalb nicht noch einmal. Der Leerzustand behält
 * seinen Knopf: Wer auf eine leere Liste schaut, soll den nächsten Schritt
 * dort finden, wo er hinsieht.
 *
 * DREI SPALTEN STATT EINER STATUSSPALTE, und das ist der sichtbare Teil von
 * AUF-12: „Erbringung und Berechnung sind zwei getrennte Achsen und werden
 * getrennt geführt." Ein Auftrag über zwei Termine, von denen der erste
 * gelaufen und abgerechnet ist, steht auf *bestätigt · teilweise erbracht ·
 * teilweise berechnet* — drei Aussagen, die eine einzige Spalte nicht
 * gleichzeitig treffen könnte.
 *
 * Der Filter „noch nicht berechnet" ist die Fakturierungsliste aus AUF-20;
 * sie bekommt später ihre eigene Maske mit Fälligkeiten, und bis dahin ist
 * sie hier zu erreichen, statt gar nicht.
 */
export function Auftragsliste({
  zurKunde,
  beiOeffnen,
  beiNeu,
}: {
  /** CRM-181: der Weg zur Kundenakte, nur mit `kunde:sehen` — zugleich das Recht der Kontaktkarte. */
  readonly zurKunde?: ((id: string) => void) | undefined;
  readonly beiOeffnen: (id: string) => void;
  readonly beiNeu: () => void;
}): JSX.Element {
  const [filter, setzeFilter] = useState<Filter>(LEER);
  const [auftraege, setzeAuftraege] = useState<readonly Auftragszeile[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const laden = useCallback(async (f: Filter): Promise<void> => {
    setzeLaedt(true);
    try {
      setzeAuftraege(
        await liste({
          suche: f.suche,
          status: f.status,
          fakturierung: f.fakturierung,
          alleVersionen: f.alleVersionen,
        }),
      );
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    const kennung = setTimeout(() => void laden(filter), 250);
    return () => clearTimeout(kennung);
  }, [filter, laden]);

  const marken: readonly Filtermarke[] = [
    ...(filter.status !== ''
      ? [
          {
            text: `Status: ${STATUSNAME[filter.status]}`,
            entfernen: () => setzeFilter((f) => ({ ...f, status: '' })),
          },
        ]
      : []),
    ...(filter.fakturierung !== ''
      ? [
          {
            text: `Fakturierung: ${FAKTURIERUNGSNAME[filter.fakturierung]}`,
            entfernen: () => setzeFilter((f) => ({ ...f, fakturierung: '' })),
          },
        ]
      : []),
    ...(filter.alleVersionen
      ? [
          {
            text: 'alle Fassungen',
            entfernen: () => setzeFilter((f) => ({ ...f, alleVersionen: false })),
          },
        ]
      : []),
  ];

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt ? undefined : zaehlwort(auftraege.length, 'Auftrag', 'Aufträge'));

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      <Liste
        suche={{
          wert: filter.suche,
          aendern: (wert) => setzeFilter((f) => ({ ...f, suche: wert })),
          beschriftung: 'Aufträge suchen',
          platzhalter: 'Auftragsnummer, Betreff oder Bestellreferenz',
        }}
        filter={
          <>
            <select
              className="liste__filter"
              value={filter.status}
              onChange={(e) =>
                setzeFilter((f) => ({ ...f, status: e.target.value as Auftragsstatus | '' }))
              }
              aria-label="Status"
            >
              <option value="">Alle Status</option>
              {(Object.keys(STATUSNAME) as Auftragsstatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUSNAME[s]}
                </option>
              ))}
            </select>
            {/* AUF-20 */}
            <select
              className="liste__filter"
              value={filter.fakturierung}
              onChange={(e) =>
                setzeFilter((f) => ({ ...f, fakturierung: e.target.value as Fakturierung | '' }))
              }
              aria-label="Fakturierung"
            >
              <option value="">Jede Fakturierung</option>
              {(Object.keys(FAKTURIERUNGSNAME) as Fakturierung[]).map((s) => (
                <option key={s} value={s}>
                  {FAKTURIERUNGSNAME[s]}
                </option>
              ))}
            </select>
            {/* AUF-14 */}
            <label className="liste__schalter">
              <input
                type="checkbox"
                checked={filter.alleVersionen}
                onChange={(e) => setzeFilter((f) => ({ ...f, alleVersionen: e.target.checked }))}
              />
              alle Fassungen
            </label>
          </>
        }
        marken={marken}
        hinweise={
          /* AUF-13 */
          <p className="hilfe">
            Erbringung und Berechnung stehen getrennt: Ein Auftrag kann teilweise erbracht
            und zugleich vollständig berechnet sein. Beide werden aus den Positionen fortgeschrieben
            und nicht von Hand gesetzt.
          </p>
        }
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">{auftraege.length} Aufträge</caption>
            <thead>
              <tr>
                <th scope="col">Nummer</th>
                <th scope="col">Kunde</th>
                <th scope="col">Betreff</th>
                <th scope="col">Datum</th>
                <th scope="col">Status</th>
                <th scope="col">Erbringung</th>
                <th scope="col">Fakturierung</th>
                <th scope="col" className="zahl">
                  Brutto
                </th>
              </tr>
            </thead>
            <tbody>
              {auftraege.map((a) => (
                <tr key={a.id}>
                  <td>
                    <button
                      type="button"
                      className="liste__oeffnen liste__nummer"
                      onClick={() => beiOeffnen(a.id)}
                    >
                      {a.auftragsnummer}
                    </button>
                    {a.version > 1 && <span className="kanalliste__name"> · V{a.version}</span>}
                    {a.herkunft === 'angebot' && (
                      <span className="kanalliste__name"> · {HERKUNFTSNAME[a.herkunft]}</span>
                    )}
                  </td>
                  <td className="liste__name">
                  <KundeVerweis kundeId={a.kunde} zurAkte={zurKunde}>
                    {a.kundenname}
                  </KundeVerweis>
                </td>
                  <td>{a.betreff}</td>
                  <td className="liste__einzeilig">{alsTag(a.belegdatum)}</td>
                  <td>
                    <span className={markenklasse(STATUSMARKE[a.status])}>
                      {STATUSNAME[a.status]}
                    </span>
                  </td>
                  <td>{ERBRINGUNGSNAME[a.erbringung]}</td>
                  <td>
                    <span className={markenklasse(FAKTURIERUNGSMARKE[a.fakturierung])}>
                      {FAKTURIERUNGSNAME[a.fakturierung]}
                    </span>
                  </td>
                  <td className="zahl">{alsEuro(a.belegbrutto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!laedt && auftraege.length === 0 && (
          <div className="liste__leer" role="status">
            {/* AUF-07 */}
            <p>
              {filter.suche === '' && filter.status === '' && filter.fakturierung === ''
                ? 'Noch kein Auftrag. Aufträge entstehen aus einem angenommenen Angebot oder werden hier erfasst.'
                : `Kein Treffer${filter.suche === '' ? '' : ` für „${filter.suche}“`}.`}
            </p>
            <button type="button" className="knopf knopf--haupt" onClick={beiNeu}>
              Auftrag anlegen
            </button>
          </div>
        )}
      </Liste>
    </>
  );
}
